// Single source of truth for how a Rego dry-run verdict is DISPLAYED in the intent authoring
// surfaces (the Simulate panels). It mirrors the backend's typed outcome: `kind` is ALLOW/DENY/
// UNKNOWN and `failure` is the typed cause (NONE when the verdict is the policy's own judgement).
//
// The reason this exists: a POLICY_CONFLICT (two `allow` rules produce different outputs for the
// same call — an authoring defect) comes back as kind=DENY, so a raw `kind` chip would mislabel a
// broken policy as a deliberate denial. Both Simulate panels now derive their chip from here, so
// the distinct conflict state is shown the same way in one place rather than re-encoded per screen.

/**
 * @param {string} kind     RegoDecision.kind — ALLOW | DENY | UNKNOWN (+ FE pseudo-kind ERROR)
 * @param {string} [failure] RegoDecision.failure — NONE | POLICY_CONFLICT | REMOTE_ERROR | …
 * @returns {{tone: 'allow'|'deny'|'warn'|'conflict', label: string, isConflict: boolean}}
 */
export function regoVerdictView(kind, failure) {
  if (failure === 'POLICY_CONFLICT') {
    return { tone: 'conflict', label: 'RULE CONFLICT', isConflict: true };
  }
  switch (String(kind || 'UNKNOWN').toUpperCase()) {
    case 'ALLOW': return { tone: 'allow', label: 'ALLOW', isConflict: false };
    case 'DENY':  return { tone: 'deny',  label: 'DENY', isConflict: false };
    case 'ERROR': return { tone: 'deny',  label: 'ERROR', isConflict: false };
    default:      return { tone: 'warn',  label: String(kind || 'UNKNOWN').toUpperCase(), isConflict: false };
  }
}

/** tone → theme tokens (conflict reuses the danger palette but is labelled distinctly). */
export const REGO_TONE_COLORS = {
  allow:    { color: 'var(--v2-success)', bg: 'var(--v2-success-soft)' },
  deny:     { color: 'var(--v2-danger)',  bg: 'var(--v2-danger-soft)' },
  warn:     { color: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)' },
  conflict: { color: 'var(--v2-danger)',  bg: 'var(--v2-danger-soft)' },
};

/** A short author-facing hint appended for the conflict state; empty otherwise. */
export function regoConflictHint(failure) {
  return failure === 'POLICY_CONFLICT'
    ? 'Two allow rules match the same call and return different results — guard the branches so they are mutually exclusive.'
    : '';
}
